var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Shipment List Component
 * Displays list of shipments from bin (AKA container) scan
 */
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineKeyboardBackspace } from 'react-icons/md';
import Table from '@fp-suite/core-ui/src/components/Table';
import Button from '@fp-suite/core-ui/src/components/Button';
import { usePackingState } from '../../../hooks/packing';
import { StatusBadge } from '../shared';
import { PackingSubscreen, ShipmentStatus } from '../types';
export var ShipmentList = function () {
    var t = useTranslation().t;
    var _a = usePackingState(), shipmentList = _a.shipmentList, shipmentListPagination = _a.shipmentListPagination, containerId = _a.containerId, shipmentStatusList = _a.shipmentStatusList, loadShipmentDetails = _a.loadShipmentDetails, loadShipmentList = _a.loadShipmentList, navigateToSubscreen = _a.navigateToSubscreen, toggleScanThroughPack = _a.toggleScanThroughPack;
    // Handle shipment selection (Pack button click)
    var handleSelectShipment = useCallback(function (shipmentId) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, loadShipmentDetails(shipmentId)];
                case 1:
                    _a.sent();
                    toggleScanThroughPack(true);
                    return [2 /*return*/];
            }
        });
    }); }, [loadShipmentDetails, toggleScanThroughPack]);
    // Handle back navigation
    var handleBack = useCallback(function () {
        navigateToSubscreen(PackingSubscreen.ShipmentId);
    }, [navigateToSubscreen]);
    var loadNextProds = useCallback(function (index) { return __awaiter(void 0, void 0, void 0, function () {
        var pageIndex;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(containerId && index > 0)) return [3 /*break*/, 2];
                    pageIndex = index - 1;
                    if (!(pageIndex <= shipmentListPagination.pageMaxIndex)) return [3 /*break*/, 2];
                    return [4 /*yield*/, loadShipmentList(containerId, pageIndex)];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2: return [2 /*return*/];
            }
        });
    }); }, [containerId, loadShipmentList, shipmentListPagination.pageMaxIndex]);
    // Get status description from shipmentStatusList
    var getStatusDescription = useCallback(function (statusId) {
        var status = shipmentStatusList.find(function (s) { return s.statusId === statusId; });
        return (status === null || status === void 0 ? void 0 : status.description) || statusId;
    }, [shipmentStatusList]);
    // Check if Pack button should be disabled based on status
    var isPackDisabled = useCallback(function (statusId) {
        if (!statusId)
            return false;
        return [ShipmentStatus.Packed, ShipmentStatus.Shipped, ShipmentStatus.Delivered].includes(statusId);
    }, []);
    // Table columns matching original Packing.tsx style
    var shipmentListColumns = useMemo(function () { return [
        {
            title: (_jsx("div", __assign({ className: 'flex items-center relative h-5' }, { children: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Shipment') }), void 0) }), void 0)),
            render: function (shipmentId) { return _jsx("h1", __assign({ className: 'font-medium text-sm' }, { children: shipmentId }), void 0); },
            dataIndex: 'shipmentId'
        },
        {
            title: (_jsx("div", __assign({ className: 'flex items-center relative h-5' }, { children: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Order') }), void 0) }), void 0)),
            render: function (orderId) { return _jsx("h1", __assign({ className: 'font-medium text-sm' }, { children: orderId }), void 0); },
            dataIndex: 'orderId'
        },
        {
            title: (_jsx("div", __assign({ className: 'flex items-center relative h-5' }, { children: _jsx("p", __assign({ className: 'mr-1' }, { children: t('PickList') }), void 0) }), void 0)),
            render: function (workEffortId) { return _jsx("h1", __assign({ className: 'font-medium text-sm' }, { children: workEffortId }), void 0); },
            dataIndex: 'workEffortId'
        },
        {
            title: (_jsx("div", __assign({ className: 'flex items-center relative h-5' }, { children: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Status') }), void 0) }), void 0)),
            render: function (statusId) { return (_jsx(StatusBadge, { status: statusId, statusDescription: getStatusDescription(statusId) }, void 0)); },
            dataIndex: 'statusId'
        },
        {
            title: (_jsx("div", __assign({ className: 'flex items-center relative h-5' }, { children: _jsx("p", { className: 'mr-1' }, void 0) }), void 0)),
            render: function (shipmentId, data) { return (_jsx(Button, { isDisabled: isPackDisabled(data === null || data === void 0 ? void 0 : data.statusId), buttonText: t('Pack'), onClick: function () { return handleSelectShipment(shipmentId); } }, void 0)); },
            dataIndex: 'shipmentId'
        }
    ]; }, [t, handleSelectShipment, getStatusDescription, isPackDisabled]);
    return (_jsxs("div", __assign({ className: 'w-full' }, { children: [_jsxs("div", __assign({ className: 'flex flex-row items-center mb-4 mt-2 cursor-pointer w-max', onClick: handleBack }, { children: [_jsx(MdOutlineKeyboardBackspace, { style: { fontSize: '20px' }, className: 'text-primary' }, void 0), _jsx("p", __assign({ className: 'ml-1 sm:ml-2 text-xs font-bold text-primary' }, { children: t('GO BACK') }), void 0)] }), void 0), _jsx("div", __assign({ className: 'bg-white p-5 pb-2 rounded-lg shadow-sm' }, { children: _jsx(Table, { isPaginated: true, columns: shipmentListColumns, dataSource: shipmentList, isServerRendered: true, loadNextProds: loadNextProds, recordCount: shipmentListPagination.recordCount }, 'shipmentlist') }), void 0)] }), void 0));
};
export default ShipmentList;
